import {
  MutationCache,
  QueryClient,
  defaultShouldDehydrateQuery,
  isServer,
} from '@tanstack/react-query';
import { getFriendlyErrorMessage } from './error-messages';
import { notify } from './notifier';
import {
  getApiEndpoint,
  getAppInfo,
  Nullable,
  UserWithToken,
} from '@ygoargentina/sdk/api/api.types';
import { ClientError, GraphQLClient } from 'graphql-request';
import { authService } from './auth-service';

export const getGraphQLEndpoint = () => `${getApiEndpoint()}/graphql`;

// Detect if running in React Native (no cookies, different auth model)
const isReactNative =
  typeof navigator !== 'undefined' && navigator.product === 'ReactNative';

let handleUnauthorized: Nullable<() => void> = null;
let serviceApiKey: Nullable<string> = null;

export const setUnauthorizedHandler = (handler: () => void) => {
  handleUnauthorized = handler;
};

/**
 * Qué hacer cuando el API dice que no hay sesión.
 *
 * Cada app registra lo suyo con `setUnauthorizedHandler`: folder y marketplace
 * limpian el CSRF y llevan a su home. Las públicas —web, calendar, ranking,
 * streaming— no registran nada, y acá había un fallback que navegaba a
 * `/logout`, una ruta que ninguna de ellas tiene. Con eso, un 401 de una query
 * que debía ser pública (`superYacsInvitations`, que se le olvidó a la
 * whitelist) terminaba sacando al visitante de la landing y dejándolo en un
 * 404, sin nada que lo explicara.
 *
 * Sin handler no hay a dónde mandarlo: el error se propaga al caller —que en
 * las públicas ya lo atrapa— y la página sigue viva y anónima.
 */
export const notifyUnauthorized = () => {
  handleUnauthorized?.();
};

/**
 * Set an API key that will be sent with all GraphQL requests.
 * This key is used for service-level authentication (e.g., tournament app acting as organizer).
 */
export const setServiceApiKey = (apiKey: string | null) => {
  serviceApiKey = apiKey;
};

// Check if an error is a network/connection error (API might be restarting)
const isNetworkError = (error: unknown): boolean => {
  if (!error) return false;

  const err = error as Error;
  const message = err.message?.toLowerCase() || '';

  // Common network error patterns
  if (
    message.includes('failed to fetch') ||
    message.includes('network') ||
    message.includes('econnrefused') ||
    message.includes('econnreset') ||
    message.includes('timeout') ||
    message.includes('aborted') ||
    err.name === 'AbortError' ||
    err.name === 'TypeError' // fetch throws TypeError on network failure
  ) {
    return true;
  }

  return false;
};

// Check if an error is a 401 authentication error
// Only returns true for actual auth errors, not network errors
export const isAuthError = (error: unknown): boolean => {
  if (!error) return false;

  // Don't treat network errors as auth errors - API might just be restarting
  if (isNetworkError(error)) {
    return false;
  }

  // Check ClientError from graphql-request
  if (error instanceof ClientError) {
    // Only trust actual HTTP 401 status
    if (error.response?.status === 401) return true;

    // Check for GraphQL errors with unauthenticated message
    const errors = error.response?.errors;
    if (
      errors?.some(
        (e: { message?: string }) =>
          e.message?.toLowerCase().includes('unauthenticated') ||
          e.message?.toLowerCase().includes('unauthorized')
      )
    ) {
      return true;
    }
  }

  return false;
};

/**
 * Handles GraphQL auth errors. Returns true if the session was refreshed
 * and the caller should retry the request.
 */
const handleGraphQLError = async (error: unknown): Promise<boolean> => {
  if (isAuthError(error)) {
    // Try to refresh the session before giving up (web only - React Native handles auth differently)
    if (!isReactNative) {
      const refreshed = await authService.refreshSession();
      if (refreshed) {
        return true; // Signal caller to retry with refreshed session
      }
    }

    notifyUnauthorized();
  }
  return false;
};

// GraphQL request deadline. During SSR we must abort with enough headroom for
// React render + HTML serialization to still finish inside Vercel's 10s
// function limit — otherwise a slow API turns into a hard 504 instead of a
// graceful degraded render.
//
// En el browser NO imponemos deadline. El de 8s abortaba requests que seguían
// en fase de conexión: el server nunca las veía (0 bytes, nada en los logs) y
// el usuario recibía un error por algo que habría completado solo. Peor en
// mutations, que no son idempotentes: la escritura podía aplicarse igual y el
// front la daba por fallida. La cancelación real la sigue manejando quien
// llama (TanStack Query aborta al desmontar o al superseder una query).
const SERVER_REQUEST_TIMEOUT = 6000;
const shouldApplyTimeout = isServer && !isReactNative;

// Marca nuestro propio abort para distinguirlo de una cancelación del caller,
// que no es un error que haya que mostrarle a nadie.
const TIMEOUT_REASON = 'ygoarg:request-timeout';

/**
 * Une el signal del caller con el nuestro sin descartar ninguno. `AbortSignal.any`
 * no existe en runtimes viejos (Hermes/Safari <17.4), así que caemos a un
 * controller propio que reenvía el primer abort que ocurra.
 */
const combineSignals = (
  callerSignal: Nullable<AbortSignal>,
  timeoutSignal?: AbortSignal
): AbortSignal | null => {
  if (!callerSignal) return timeoutSignal ?? null;
  if (!timeoutSignal) return callerSignal;

  const signals = [callerSignal, timeoutSignal];

  if (typeof AbortSignal !== 'undefined' && 'any' in AbortSignal) {
    return (
      AbortSignal as unknown as { any: (s: AbortSignal[]) => AbortSignal }
    ).any(signals);
  }

  const merged = new AbortController();
  for (const signal of signals) {
    if (signal.aborted) {
      merged.abort(signal.reason);
      break;
    }
    signal.addEventListener('abort', () => merged.abort(signal.reason), {
      once: true,
    });
  }
  return merged.signal;
};

export const getGraphQLQueryClient = (user: Nullable<UserWithToken>) => {
  const headers: Record<string, string> = {};

  // React Native uses X-Ext-API-Key for authentication (permanent API key)
  // Web uses cookies (credentials: 'include')
  if (isReactNative && user?.accessToken) {
    headers['X-Ext-API-Key'] = user.accessToken;
  }

  // Service API key for elevated permissions (e.g., tournament app)
  // This takes precedence over user's API key when set
  if (serviceApiKey) {
    headers['X-Ext-API-Key'] = serviceApiKey;
  }

  // CSRF token for mutations (only on web, React Native handles auth differently)
  if (!isReactNative) {
    const csrfToken = authService.getCsrfToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
  }

  // App identification headers
  const appInfo = getAppInfo();
  if (appInfo) {
    headers['X-ygoarg-service'] = appInfo.service;
    headers['X-ygoarg-app-version'] = appInfo.version;
    if (appInfo.commitHash) {
      headers['X-ygoarg-app-hash'] = appInfo.commitHash;
    }
  }

  const client = new GraphQLClient(getGraphQLEndpoint(), {
    headers,
    // Add timeout and credentials to support cookie-based auth (web only)
    fetch: (url, options) => {
      const timeoutController = shouldApplyTimeout ? new AbortController() : null;
      const timeoutId = timeoutController
        ? setTimeout(
            () => timeoutController.abort(TIMEOUT_REASON),
            SERVER_REQUEST_TIMEOUT
          )
        : null;

      // Encadenamos el signal de quien llama en vez de pisarlo. Antes se hacía
      // `signal: controller.signal` DESPUÉS del spread de `options`, lo que
      // descartaba el signal de TanStack Query: ninguna request se cancelaba
      // nunca al desmontar un componente, y quedaban vivas ocupando conexión.
      const callerSignal = options?.signal ?? null;
      const signal = combineSignals(callerSignal, timeoutController?.signal);

      return fetch(url, {
        ...options,
        // Only include credentials on web - React Native doesn't use cookies
        ...(isReactNative
          ? {}
          : { credentials: 'include' as RequestCredentials }),
        ...(signal ? { signal } : {}),
      }).finally(() => {
        if (timeoutId) clearTimeout(timeoutId);
      });
    },
  });

  // `request` tiene una firma sobrecargada (documento + variables, u objeto de
  // opciones) que no se puede reimplementar sin repetirla entera. Como este
  // wrapper no inspecciona ni transforma los argumentos —solo los reenvía—
  // alcanza con reenviarlos opacos. El cast del final es el único punto no
  // verificado, y es mejor que tipar los tres parámetros como `any`: así el
  // cuerpo no puede tocar los valores por accidente.
  type RequestFn = typeof client.request;
  const forward = client.request.bind(client) as (
    ...args: unknown[]
  ) => Promise<unknown>;

  client.request = (async (...args: unknown[]) => {
    try {
      return await forward(...args);
    } catch (error) {
      // Solo traducimos a "timeout" nuestro propio abort (SSR). Un AbortError
      // del caller es una cancelación normal — se propaga tal cual para que
      // TanStack Query la trate como cancelada y no como fallo.
      if (
        error instanceof Error &&
        error.name === 'AbortError' &&
        (error as Error & { cause?: unknown }).cause === TIMEOUT_REASON
      ) {
        throw new Error('Request timeout - please try again');
      }
      const shouldRetry = await handleGraphQLError(error);
      if (shouldRetry) {
        // Session was refreshed — retry the original request once
        return await forward(...args);
      }
      throw error;
    }
  }) as RequestFn;

  return client;
};

const MUTATION_ERROR_FALLBACK =
  'No se pudo completar la acción. Probá de nuevo en un momento.';

/**
 * Red de contención para las mutations que nadie manejó (YGO-279).
 *
 * El 21/07/2026 una mutation de torneo falló y el usuario no vio **nada**: ni
 * spinner, ni error, ni cambio en la UI. Se arreglaron los seis call sites
 * rotos ese día, pero nada impedía que la próxima mutation naciera igual de
 * muda — dependía de acordarse.
 *
 * Esto lo resuelve en la capa de abajo: el `onError` del `MutationCache` corre
 * para TODA mutation del cliente, exista hoy o se escriba mañana, sin tocar un
 * solo call site.
 *
 * El guard es la parte importante: TanStack ejecuta este handler ADEMÁS del
 * `onError` de la mutation, así que sin él las que ya avisan mostrarían dos
 * toasts. Si el call site define su propio `onError`, se asume que ya avisó y
 * acá no se hace nada.
 */
export const createDefaultMutationCache = () =>
  new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.onError) return;
      notify.error(getFriendlyErrorMessage(error, MUTATION_ERROR_FALLBACK));
    },
  });

function makeQueryClient() {
  return new QueryClient({
    mutationCache: createDefaultMutationCache(),
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        retry: (failureCount, error) => {
          // No retries during SSR: each attempt can burn the full request
          // timeout, and several attempts blow past Vercel's 10s function
          // limit → hard 504. Render once on the server (degraded if the API
          // is slow/down) and let the client refetch after hydration.
          if (isServer) return false;
          if (error instanceof ClientError && error.response?.status === 401) {
            return false;
          }
          return failureCount < 3;
        },
      },
      mutations: {
        retry: false,
      },
      dehydrate: {
        // include pending queries in dehydration
        shouldDehydrateQuery: (query) =>
          defaultShouldDehydrateQuery(query) ||
          query.state.status === 'pending',
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

export function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

export function getErrorKey(error: Error) {
  const innerError = error as Error & {
    response: { errors: { message: string }[] };
  };

  let errorKey = '';

  try {
    if (innerError?.response?.errors?.[0]?.message) {
      errorKey = innerError.response.errors[0].message;
    } else if (typeof innerError.message === 'string') {
      errorKey = innerError.message;
    }
  } catch (_) {
    errorKey = '';
  }

  return errorKey;
}
